import { z } from 'zod';
import {
  TTS_PROVIDERS,
  TTS_MODES,
  TTS_OUTPUT_FORMATS,
  QWEN3_SPEAKER_IDS,
  QWEN3_LANGUAGE_CODES,
} from './options';

// --- 基本 enum schemas ---
export const TTSProviderSchema = z.enum(TTS_PROVIDERS);
export const TTSModeSchema = z.enum(TTS_MODES);
export const TTSOutputFormatSchema = z.enum(TTS_OUTPUT_FORMATS);

export const Qwen3SpeakerSchema = z.enum(QWEN3_SPEAKER_IDS);
export const Qwen3LanguageSchema = z.enum(QWEN3_LANGUAGE_CODES);

// --- プロバイダ別 Selection (永続化される選択状態) ---
export const Qwen3SelectionSchema = z.object({
  provider: z.literal('qwen3'),
  mode: TTSModeSchema,
  speaker: Qwen3SpeakerSchema,
  language: Qwen3LanguageSchema,
  style: z.string().optional(),
  voiceDescription: z.string().optional(),
  refAudioPoolId: z.string().optional(),
  outputFormat: TTSOutputFormatSchema,
});

export const BertVits2SelectionSchema = z.object({
  provider: z.literal('bert-vits2'),
  modelId: z.string(),
  modelName: z.string(),
  speakerName: z.string(),
  style: z.string(),
});

export const IrodoriSelectionSchema = z.object({
  provider: z.literal('irodori'),
  voice: z.string(),
  speed: z.number().min(0.25).max(4.0),
  numSteps: z.number().int().min(1).max(64).optional(),
  cfgScaleText: z.number().min(0).max(10).optional(),
  cfgScaleSpeaker: z.number().min(0).max(10).optional(),
  seed: z.number().int().nullable().optional(),
  responseFormat: z.literal('wav').optional(),
  /**
   * VoiceDesign の指示文 (日本語)。感情・スタイル・環境だけを書く。
   * 声質を書くと参照音声と食い違って音が崩れる (モデルカードの注意書き)。
   */
  caption: z.string().optional(),
  cfgScaleCaption: z.number().min(0).max(10).optional(),
  /** 台詞の擬音から制御絵文字を自動で差し込むか。未指定は入れる。 */
  autoEmoji: z.boolean().optional(),
  /**
   * 参照音声として使う秒数の上限。
   * v4 既定の 120 秒は前処理が重く、長い参照だと 1 文で 10 秒以上かかる。
   */
  maxRefSeconds: z.number().min(1).max(120).optional(),
});

export const TTSSelectionSchema = z.discriminatedUnion('provider', [
  Qwen3SelectionSchema,
  BertVits2SelectionSchema,
  IrodoriSelectionSchema,
]);

// --- 合成リクエスト (Selection + transient runtime data) ---
export const TTSSynthesisRequestSchema = z.object({
  selection: TTSSelectionSchema,
  text: z.string().min(1),
  serverUrl: z.string().url().optional(),
  // Qwen3 cloneモード用 (in-memory blob, persistされない)
  // 注: z.instanceof(Blob) はブラウザ環境専用。本スキーマはクライアントサイドのみで使用する想定。
  refAudioBlob: z.instanceof(Blob).optional(),
  refFileName: z.string().optional(),
  refText: z.string().optional(),
});

// --- 推論型 ---
export type TTSProvider = z.infer<typeof TTSProviderSchema>;
export type TTSMode = z.infer<typeof TTSModeSchema>;
export type TTSOutputFormat = z.infer<typeof TTSOutputFormatSchema>;
export type Qwen3Speaker = z.infer<typeof Qwen3SpeakerSchema>;
export type Qwen3Language = z.infer<typeof Qwen3LanguageSchema>;
export type Qwen3Selection = z.infer<typeof Qwen3SelectionSchema>;
export type BertVits2Selection = z.infer<typeof BertVits2SelectionSchema>;
export type IrodoriSelection = z.infer<typeof IrodoriSelectionSchema>;
export type TTSSelection = z.infer<typeof TTSSelectionSchema>;
export type TTSSynthesisRequest = z.infer<typeof TTSSynthesisRequestSchema>;

// --- デフォルト値 ---
export const DEFAULT_QWEN3_SELECTION: Qwen3Selection = {
  provider: 'qwen3',
  mode: 'synthesize',
  speaker: 'ono_anna',
  language: 'Japanese',
  outputFormat: 'wav',
};

export const DEFAULT_IRODORI_SELECTION: IrodoriSelection = {
  provider: 'irodori',
  voice: 'none',
  speed: 1.0,
  numSteps: 24,
  cfgScaleText: 3.0,
  cfgScaleSpeaker: 5.0,
  seed: null,
  responseFormat: 'wav',
};

export const DEFAULT_TTS_SELECTION: TTSSelection = DEFAULT_QWEN3_SELECTION;

// --- 寛容parse: 永続化データ復元時に使用 ---
// enum違反 (削除済みspeakerなど) があれば DEFAULT_TTS_SELECTION にフォールバック。
export function parseStoredSelection(raw: unknown): TTSSelection {
  const result = TTSSelectionSchema.safeParse(raw);
  // フォールバック時は DEFAULT_TTS_SELECTION の shallow copy を返す
  // (caller の意図しない mutation が DEFAULT を破壊するのを防ぐ)
  return result.success ? result.data : { ...DEFAULT_TTS_SELECTION };
}
